<script>
    import { getContext } from 'svelte';
    import { Policy } from "@Store";
    import Question from "@components/Question.svelte";
    import 'animate.css';
    import { computeMedicalMalpracticeValid } from '@routes/validators/validator';
    const CONTEXT = getContext('CONTEXT');

    $: questions = $Policy.questions ? $Policy.questions : [];
    $: valid = $Policy.valid;

    // APPLICANT QUESTIONS SECTION

    // Create a lookup map in one pass - much more efficient!
    $: questionMap = questions.reduce((map, question) => {
        map[question.question] = question;
        return map;
    }, {});

    // Now you can access any question efficiently
    $: ({
        'Do you require Medical Malpractice Insurance?': medicalmalpractice_question,
        'What coverage are you seeking?': coverage_question,
        'Do you currently have an Individual Medical Malpractice policy with IPMGA?': individual_medical_malpractice_policy_question,
        'What is your current Individual Malpractice Limit': individual_medical_malpractice_limit_question,
        'Clinic Medical Malpractice Limit Requested': clinic_medical_malpractice_limit_requested_question,
        'Clinic Abuse Limit': clinic_abuse_limit_question,
        'Please select all modalities offered at the clinic:': modalities_question,
        'Specify other': other_modalities_description_question,
        'Individual Medical Malpractice Limit Requested': individual_medical_malpractice_limit_requested_question,
        'Abuse Limit': individual_abuse_limit_question,
        'Medical Malpractice Insurer': medical_malpractice_insurer_question,
        'Medical Malpractice Expiry Date (If no previous coverage please enter desired effective date)': medical_malpractice_expiry_date_question,
        'Medical Malpractice Retroactive Date (if applicable, if date is unknown, please supply date licensed as an ND)': medical_malpractice_retroactive_date_question,
        'Do you manufacture, repackage, or relabel any products?': man_q,

        'Does the Establishment ensure that all of its healthcare staff maintain their own malpractice liability?': establishment_q,
        'As part of the practitioner credentialing process, is evidence of this coverage required on an annual basis?': annual_basis_q,
        'Is informed consent obtained prior to all medical procedures/treatments/tests etc.?': informed_consent_q,
        'Do you follow the current guidance for infection prevention & control issued by the Public Health Agency of Canada or Provincial Health Agency (i.e. – IPAC, SHARPS, safe collection, storage and disposal of hazardous waste, etc.)?': infection_prevention_q,
    } = questionMap);

    // Create named values lookup map - ONE iteration instead of 9!
    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    // Check if they need medical malpractice
    $: needsMedicalMalpractice = namedValuesMap['med_required']?.toLowerCase() === 'yes';

    // the named value is a string formatted as a json, we need the state item to == bc
    $: mailing_address_in_bc = (() => {
        try {
            const addr = namedValuesMap['address_mailing'];
            return addr ? JSON.parse(addr)?.state == 'British Columbia' : false;
        } catch {
            return false;
        }
    })()

    // Helpers to read answers by Question object
    $: getAnswerText = (q) => (q && $Policy.answers?.[q.id]?.text_answer || '').trim();
    $: isAnsweredById = (q) => !!getAnswerText(q);

    // Coverage parsing
    $: coverageText = getAnswerText(coverage_question).toLowerCase();
    $: needsClinicOnly = coverageText.includes('clinic');
    $: needsClinic = coverageText.includes('clinic') || coverageText.includes('both');
    $: needsIndividual = coverageText.includes('individual') || coverageText.includes('both');

    // Show sections based on coverage selection
    $: show_clinic_questions = needsClinic;
    $: show_individual_questions = needsIndividual;

    $: individual_medical_malpractice_policy_answered = isAnsweredById(individual_medical_malpractice_policy_question);
    $: ask_individual_medical_malpractice_limit = individual_medical_malpractice_policy_answered && getAnswerText(individual_medical_malpractice_policy_question).toLowerCase().includes('yes');
    $: individual_medical_malpractice_limit_answered = isAnsweredById(individual_medical_malpractice_limit_question);

    $: show_other_modalities_description_question = (() => {
        if (!modalities_question || !$Policy.answers) return false;
        
        const modalities_answer = $Policy.answers[modalities_question.id];
        console.log(modalities_answer);
        if (!modalities_answer) return false;
        
        const answerText = modalities_answer.text_answer || '';
        
        // Split by comma and check if any value equals "other" (case-insensitive)
        const selections = answerText.split(',').map(s => s.trim().toLowerCase());
        return selections.includes('other');
    })();


    $: medicalmalpractice_valid = computeMedicalMalpracticeValid($Policy);

    // Update CONTEXT when validation changes
    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            medicalmalpractice: medicalmalpractice_valid
        }));
    }

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e); 
    }


</script>

<div class="page-container">
    <div class="questions-wrapper">
        <!-- APPLICANT QUESTIONS -->
        {#if medicalmalpractice_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={medicalmalpractice_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>
        {/if}
        
        {#if needsMedicalMalpractice && coverage_question}
            <div class="animate__animated animate__fadeInUp">
                <Question question={coverage_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            </div>

            {#if man_q}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={man_q} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}

            {#if show_clinic_questions}
                {#if individual_medical_malpractice_policy_question && needsClinicOnly}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={individual_medical_malpractice_policy_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                {#if ask_individual_medical_malpractice_limit && needsClinicOnly}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={individual_medical_malpractice_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={clinic_medical_malpractice_limit_requested_question} required={true} remove_options={mailing_address_in_bc ? [0, 1] : []} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
                {#if clinic_abuse_limit_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={clinic_abuse_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
                {#if modalities_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={modalities_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                    {#if show_other_modalities_description_question}
                        <div class="animate__animated animate__fadeInUp">
                            <Question question={other_modalities_description_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    {/if}
                {/if}
            {/if}

            {#if show_individual_questions}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={individual_medical_malpractice_limit_requested_question} required={true} remove_options={mailing_address_in_bc ? [0, 1] : []} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
                {#if individual_abuse_limit_question}
                    <div class="animate__animated animate__fadeInUp">
                        <Question question={individual_abuse_limit_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    </div>
                {/if}
            {/if}

            {#if medical_malpractice_insurer_question && medical_malpractice_expiry_date_question && medical_malpractice_retroactive_date_question}
                <div class="section-container animate__animated animate__fadeInUp">
                    <div class="section-header">
                        <div class="section-icon">🏥</div>
                        <h1>Previous Medical Malpractice Coverage</h1>
                    </div>

                    <div class="sub-questions">
                        <div class="sub-question">
                            <Question question={medical_malpractice_insurer_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>

                        <div class="sub-question">
                            <Question question={medical_malpractice_expiry_date_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                        <div class="sub-question">
                            <Question question={medical_malpractice_retroactive_date_question} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                        </div>
                    </div>
                </div>
            {/if}

            {#if establishment_q}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={establishment_q} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if annual_basis_q}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={annual_basis_q} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if informed_consent_q}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={informed_consent_q} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
            {#if infection_prevention_q}
                <div class="animate__animated animate__fadeInUp">
                    <Question question={infection_prevention_q} required={true} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                </div>
            {/if}
        {/if}
    </div>
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }

    /* Section Container Styles */
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }

    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .sub-question {
        position: relative;
    }

    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }

        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }

        .sub-question {
            padding-left: 1rem;
        }

        .sub-question::before {
            width: 3px;
        }
    }
</style>